import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import { useField } from "formik";
import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import { selectCurrentSection, selectEcrf } from "features/ecrf/redux/ecrfSlice";
import ConfirmationAction from "components/ConfirmationAction";
import { useParams } from "react-router-dom";
import { Dialog, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useSnackbar } from "notistack";
import {useTranslation} from "react-i18next";

export interface DicomButtonFormikProps
  extends Omit<ButtonProps, "name" | "value" | "onChange" | "onBlur" | "error"> {
  name: string;
  link: string;
  helperText?: string;
  buttonText?: string;
  options: Array<{ key: string; value: string }>;
  extra: string;
  data: any;
  status: boolean;
}

type EcrfFormParams = {
  id: string;
};

const CloseObservationFormik = ({
  name,
  link,
  helperText,
  buttonText,
  options,
  extra,
  data,
  status,
  ...props
}: DicomButtonFormikProps) => {
  const dispatch = useAppDispatch();
  const ecrf = useAppSelector(selectEcrf);
  const [field, meta] = useField(name);
  const [isDisabled, setIsDisabled] = useState(status);
  const [text, setText] = useState(buttonText);
  const { id } = useParams<EcrfFormParams>();
  const { enqueueSnackbar } = useSnackbar();
  const currentSection = useAppSelector(selectCurrentSection);
  const { t } = useTranslation();

  /** handle modal options */
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  /**
   * Change button state when randomization is already done
   */
  const updateButtonStatus = () => {
    if (currentSection?.status === 3) {
      setIsDisabled(true);
      setText(`${t('closed')} - 09.12.2021`);
    }
  };

  /**
   * Randomize patient using button instead auto-randomization
   */
  const randomize = async () => {
    try {
      setOpen(true);
      setIsDisabled(true);
      setText(`${t('closed')} - 09.12.2021`);
    } catch (error: any) {
      enqueueSnackbar(error.detail, {variant: 'error'});
    }
  };

  useEffect(() => {
    updateButtonStatus();
  }, [ecrf, dispatch]);

  return (
    <Box pt={0}>
      <ConfirmationAction
        confirmationTitle={t('do-you-want-to-close-the-visit')}
        onClick={() => randomize()}
      >
        {({ handleClick }) => (
          <Button
            name={field.name}
            id={field.name}
            value={field.value}
            variant="contained"
            color="primary"
            onClick={handleClick}
            disabled={isDisabled || status}
            {...props}
          >
            {field.value}
            {text ?? t('close-observation-period')}
          </Button>
        )}
      </ConfirmationAction>

      <Box>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Box color="black" fontWeight="bold" fontSize="20px">
                {t('the-visit-was-closed') + ' '}
                <Box fontSize="18px">09.12.2021</Box>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} autoFocus>
              {t('ok')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CloseObservationFormik;
